import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorToast } from '../../utils/errorHandling';
import './Attendance.css';
import { getAttendance, updateAttendance, getAttendanceStats } from '../../services/onlineAttendance';
import { MdArrowBackIos, MdArrowForwardIos, MdCheckCircle, MdCancel } from 'react-icons/md';
import { toast } from 'react-toastify';
import { retreatSessions } from '../../utils/constant';
import { AiOutlineLoading } from 'react-icons/ai';

const ViewAttendance = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const courseData = localStorage.getItem("courseData")
    ? JSON.parse(localStorage.getItem("courseData"))
    : null;
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [stats, setStats] = useState({ attended: 0, missed: 0, total_sessions: 0, attendance_rate: 0 });
  const { t } = useTranslation();

  useEffect(() => {
    // Fetch attendance data
    const fetchAttendance = async () => {
      try {
        setLoading(true);
        const data = await getAttendance(courseData.id);
        if (data) {
          setAttendanceData(data.data);
        }

        // Fetch attendance statistics
        const statsData = await getAttendanceStats();
        if (statsData) {
          setStats(statsData.data);
        }

        setLoading(false);
      } catch (error) {
        errorToast(error, "Failed to fetch attendance data", t);
        setLoading(false);
      }
    };

    fetchAttendance();
  }, []);

  const handlePreviousDay = () => {
    if (currentDayIndex > 0) {
      setCurrentDayIndex(currentDayIndex - 1);
    }
  };

  const handleNextDay = () => {
    if (currentDayIndex < attendanceData.length - 1) {
      setCurrentDayIndex(currentDayIndex + 1);
    }
  };

  const handleAttendanceChange = async (sessionId, isPresent) => {
    setSavingAttendance(true);

    try {
      // Update attendance on the server
      const result = await updateAttendance(sessionId, isPresent);

      // Update local state
      const updatedData = JSON.parse(JSON.stringify(attendanceData));

      for (let i = 0; i < updatedData.length; i++) {
        const day = updatedData[i];
        for (let j = 0; j < day.sessions.length; j++) {
          const session = day.sessions[j];
          if (session.id === sessionId) {
            session.attended = isPresent;
            break;
          }
        }
      }

      setAttendanceData(updatedData);

      // Refresh attendance statistics
      try {
        const statsData = await getAttendanceStats();
        if (statsData) {
          setStats(statsData.data);
        }
      } catch (statsError) {
        console.error('Error refreshing attendance stats:', statsError);
        // Continue execution even if stats refresh fails
      }

      toast.success(t("Attendance.Updated"));
    } catch (error) {
      console.error('Error updating attendance:', error);
      errorToast(error, t("Attendance.Failed"), t);
    } finally {
      setSavingAttendance(false);
    }
  };

  const renderDaySelector = () => {
    return (
      <div className="day-selector">
        <button
          className="day-nav-button"
          onClick={handlePreviousDay}
          disabled={currentDayIndex === 0}
        >
          <MdArrowBackIos />
        </button>

        <div className="day-slider">
          {attendanceData.map((day, index) => (
            <div
              key={index}
              className={`day-indicator ${index === currentDayIndex ? 'active' : ''}`}
              onClick={() => setCurrentDayIndex(index)}
            >
              <span className="day-number">{index + 1}</span>
              {index === currentDayIndex && <span className="day-label">{t(`dayOfWeek.${day.dayOfWeek}`)}</span>}
            </div>
          ))}
        </div>

        <button
          className="day-nav-button"
          onClick={handleNextDay}
          disabled={currentDayIndex === attendanceData.length - 1}
        >
          <MdArrowForwardIos />
        </button>
      </div>
    );
  };

  return (
    <div className="attendance-container">
      <h2 className="attendance-title">{t("Attendance.Title")}</h2>
      <p className="attendance-description">{t("Attendance.Description")}</p>

      {loading ? (
        <div className='d-flex flex-column align-items-center'>
          <AiOutlineLoading className='spin' />
          <p>{t("Attendance.Loading")}</p>
        </div>
      ) : (
        <>
          <div className="attendance-summary">
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">{t("Attendance.Summary")}</h5>
                <div className="row">
                  <div className="col-md-4">
                    <div className="attendance-stat">
                      <h3>
                        {stats.attended}
                      </h3>
                      <p>{t("Attendance.SessionsAttended")}</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="attendance-stat">
                      <h3>
                        {stats.missed}
                      </h3>
                      <p>{t("Attendance.SessionsMissed")}</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="attendance-stat">
                      <h3>
                        {`${stats.attendance_rate}%`}
                      </h3>
                      <p>{t("Attendance.AttendanceRate")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {attendanceData.length > 0 && (
            <div className="daily-attendance">
              <h4>{t("Attendance.History")}</h4>

              {renderDaySelector()}

              <div className="current-day-info">
                <h5>{attendanceData[currentDayIndex].date} ({t(`dayOfWeek.${attendanceData[currentDayIndex].dayOfWeek}`)})</h5>
                <p className="attendance-instruction">{t("Attendance.recordInstruction")}</p>
              </div>

              <div className="table-responsive">
                <table className="table table-striped attendance-table">
                  <thead>
                    <tr>
                      <th>{t("Attendance.Session")}</th>
                      <th>{t("Attendance.Status")}</th>
                      <th>{t("Attendance.Action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceData[currentDayIndex].sessions.map((session) => (
                      <tr key={session.id}>
                        <td>
                          <strong>{t(`Attendance.${session.type.replace(/ /g, '')}`)}</strong>
                          <br />
                          {retreatSessions(t).find(i => i.key === session.name).title}
                          <br />
                          <span style={{ fontStyle: "italic" }}>{session.time}</span>
                        </td>
                        <td>
                          <span className={`badge ${session.attended ? 'bg-success' : session.attended === false ? 'bg-danger' : 'bg-secondary'}`}>
                            {session.attended
                              ? t("Attendance.Present")
                              : session.attended === false
                                ? t("Attendance.Absent")
                                : t("Attendance.NotMarked")}
                          </span>
                        </td>
                        <td>
                          <div className="attendance-actions">
                            <button
                              className={`attendance-action-btn present ${session.attended ? 'active' : ''}`}
                              onClick={() => handleAttendanceChange(session.id, true)}
                              disabled={savingAttendance}
                            >
                              <MdCheckCircle /> {t("Attendance.Present")}
                            </button>
                            <button
                              className={`attendance-action-btn absent ${session.attended === false ? 'active' : ''}`}
                              onClick={() => handleAttendanceChange(session.id, false)}
                              disabled={savingAttendance}
                            >
                              <MdCancel /> {t("Attendance.Absent")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ViewAttendance; 